/**
 * SAP labor posting — the timesheet → SAP gap (#63).
 *
 * Today the SAP integration only PULLS cost actuals from SAP and PUSHES budget
 * (Dassian PTW). Approved timesheet LABOR never posts back. This module is the
 * engine that closes that gap: it rolls approved labor up into SAP cost-line
 * postings (one row per WBS × cost element × fiscal period), the shape SAP CO
 * expects for an actual labor posting.
 *
 * This file is the PURE aggregator (no I/O) so it's unit-testable. The service
 * (sapIntegrationService.postTimesheetLaborToSap) reads approved labor, maps each
 * line to {wbs, costElement, cost}, runs this, and records a LaborTime sync job —
 * following the existing mock-sync demo pattern (no live SAP).
 */

/** A single approved labor line, already resolved to its charge coordinate. */
export interface LaborLineForSap {
  /** Posting target — the SAP WBS element. Lines without one can't post. */
  wbs: string | null
  /** SAP cost element; null falls back to the default labor cost element. */
  costElement?: string | null
  /** AZ calendar date the hours were worked (ISO `YYYY-MM-DD`). */
  date: string
  hours: number
  /** Fully-burdened (or base) labor cost for the line, in `currency`. */
  cost: number
  currency?: string | null
}

/** One aggregated SAP cost-line posting (mirrors sap_cost_line_items). */
export interface SapLaborPosting {
  wbsElement: string
  costElement: string
  fiscalYear: number
  postingPeriod: number // 1–12 (calendar-year fiscal calendar, v1)
  quantityHours: number
  valueCoAreaCurrency: number
  currency: string
  valueType: 'Actual'
  source: 'timesheet'
  /** How many approved labor lines rolled into this posting. */
  lineCount: number
}

export const DEFAULT_LABOR_COST_ELEMENT = '0051200000' // direct labor (YCOA)
const DEFAULT_CURRENCY = 'USD'

function periodOf(dateIso: string): { fiscalYear: number; postingPeriod: number } {
  // v1 uses a calendar-year fiscal calendar (period = month). Real SAP fiscal
  // variants (e.g. K4, V3) are a follow-up; the demo posts by calendar month.
  const [y, m] = dateIso.split('-')
  return { fiscalYear: Number(y), postingPeriod: Number(m) }
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/**
 * Roll approved labor lines into SAP cost-line postings, grouped by
 * WBS × cost element × fiscal period. Lines with no WBS can't post (no target)
 * and are returned as `skipped` so the sync job can report them.
 */
export function aggregateLaborForSapPosting(
  lines: readonly LaborLineForSap[],
): { postings: SapLaborPosting[]; skipped: LaborLineForSap[] } {
  const skipped: LaborLineForSap[] = []
  const byKey = new Map<string, SapLaborPosting>()

  for (const line of lines) {
    if (!line.wbs) { skipped.push(line); continue }
    const costElement = line.costElement || DEFAULT_LABOR_COST_ELEMENT
    const { fiscalYear, postingPeriod } = periodOf(line.date)
    const currency = line.currency || DEFAULT_CURRENCY
    const key = `${line.wbs}|${costElement}|${fiscalYear}|${postingPeriod}|${currency}`

    const existing = byKey.get(key)
    if (existing) {
      existing.quantityHours = round2(existing.quantityHours + line.hours)
      existing.valueCoAreaCurrency = round2(existing.valueCoAreaCurrency + line.cost)
      existing.lineCount += 1
    } else {
      byKey.set(key, {
        wbsElement: line.wbs,
        costElement,
        fiscalYear,
        postingPeriod,
        quantityHours: round2(line.hours),
        valueCoAreaCurrency: round2(line.cost),
        currency,
        valueType: 'Actual',
        source: 'timesheet',
        lineCount: 1,
      })
    }
  }

  // Stable, readable order: WBS, then period.
  const postings = [...byKey.values()].sort(
    (a, b) =>
      a.wbsElement.localeCompare(b.wbsElement) ||
      a.fiscalYear - b.fiscalYear ||
      a.postingPeriod - b.postingPeriod ||
      a.costElement.localeCompare(b.costElement),
  )
  return { postings, skipped }
}
